import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { MdClose, MdError, MdCheckCircle, MdWarning, MdInfo } from 'react-icons/md';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

const NotificationItem = ({ notification, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleRemove = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onRemove(notification.id), 300);
  }, [notification.id, onRemove]);

  useEffect(() => {
    if (notification.duration && notification.duration > 0) {
      const timer = setTimeout(handleRemove, notification.duration);
      return () => clearTimeout(timer);
    }
  }, [notification.duration, handleRemove]);

  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <MdCheckCircle className="text-green-500" size={20} />;
      case 'warning':
        return <MdWarning className="text-yellow-500" size={20} />;
      case 'info':
        return <MdInfo className="text-blue-500" size={20} />;
      case 'error':
      default:
        return <MdError className="text-red-500" size={20} />;
    }
  };

  const getBackgroundColor = () => {
    switch (notification.type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      case 'error':
      default:
        return 'bg-red-50 border-red-200';
    }
  };

  return (
    <div
      className={`
        transform transition-all duration-300 ease-in-out mb-3
        ${isVisible && !isExiting ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
        ${getBackgroundColor()}
        border rounded-lg shadow-lg max-w-sm w-full relative
      `}
      style={{ 
        transform: `translateX(${isVisible && !isExiting ? '0' : '100%'})`,
        opacity: isVisible && !isExiting ? 1 : 0
      }}
    >
      <div className="p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-0.5">
            {getIcon()}
          </div>
          <div className="flex-1 min-w-0">
            {notification.title && (
              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                {notification.title}
              </h4>
            )}
            <p className="text-sm text-gray-700 leading-relaxed">
              {notification.message}
            </p>
          </div>
          <button
            onClick={handleRemove}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <MdClose size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export const NotificationProvider = ({ children }) => {
  console.log('NotificationProvider rendered with children:', !!children);
  const [notifications, setNotifications] = useState([]);

  const addNotification = (notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      type: 'error',
      duration: 5000, // Default 5 seconds
      ...notification,
    };
    
    setNotifications(prev => [...prev, newNotification]);
    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  // Convenience methods for different types
  const showError = (message, options = {}) => {
    return addNotification({
      type: 'error',
      message,
      duration: 6000, // Errors stay longer
      ...options
    });
  };

  const showSuccess = (message, options = {}) => {
    return addNotification({
      type: 'success',
      message,
      duration: 4000,
      ...options
    });
  };

  const showWarning = (message, options = {}) => {
    return addNotification({
      type: 'warning',
      message,
      duration: 5000,
      ...options
    });
  };

  const showInfo = (message, options = {}) => {
    return addNotification({
      type: 'info',
      message,
      duration: 4000,
      ...options
    });
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        removeNotification,
        clearAllNotifications,
        showError,
        showSuccess,
        showWarning,
        showInfo
      }}
    >
      {children}
      
      {/* Notification Container */}
      <div className="fixed top-4 right-4 z-50 pointer-events-none">
        <div className="space-y-3">
          {notifications.map(notification => (
            <div key={notification.id} className="pointer-events-auto">
              <NotificationItem
                notification={notification}
                onRemove={removeNotification}
              />
            </div>
          ))}
        </div>
      </div>
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;